import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { CalendarOptions } from '@fullcalendar/core'
import CellChart from './CellChart'
import { useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { MultiValue, SingleValue } from 'react-select'
import { CompanyListItem, SelectItem } from '../../charts/core/_models'
import { DataObject, SeriesDataItem, meterType } from '../core/_models'
import { getCellChartData } from '../core/_requests'
import { useIntl } from 'react-intl'
import FilterFormCalendar from './FilterFormCalendar'
import ChartViewModalForCalendar from './ChartViewModalForCalendar'
import CalendarAlarmViewModal from './CalendarAlarmViewModal'
import { CustomizationContext } from '../../customization/CustomizationContext'
import { getAlertType } from '../../alarms/api/requests'
const CalendarViewPage = () => {
  const intl = useIntl()
  let [companyFilter, setCompanyFilter] = useState<SingleValue<CompanyListItem>>(null)
  let [siteFilter, setSiteFilter] = useState<MultiValue<CompanyListItem>>([])
  let [metersFilter, setMetersFilter] = useState<MultiValue<SelectItem>>([])
  let [meterTypeFilter, setMeterTypeFilter] = useState<SingleValue<meterType>>(null)
  let [selectedMeters, setSelectedMeters] = useState<MultiValue<CompanyListItem>>([])
  let [data, setData] = useState<Record<string, DataObject> | null>(null)
  let [thisYearData, setThisYearData] = useState<number[]>([0])
  let [lastYearData, setLastYearData] = useState<number[]>([0])
  const [alarmTypeFilter, setAlarmTypeFilter] = useState<'daily' | 'hourly' | null>(null)
  // @ts-ignore
  const { settings } = useContext(CustomizationContext)
  const [companyAlertType, setCompanyAlertType] = useState<'daily' | 'hourly' | 'both'>('both')

  const init = new Date()
  init.setDate(1)
  const initialDateYear = init.getFullYear()
  const initialDateMonth = String(init.getMonth() + 1).padStart(2, '0')
  const initialDateDay = String(init.getDate()).padStart(2, '0')
  const initialFormattedDate = `${initialDateYear}-${initialDateMonth}-${initialDateDay}`
  let [initialDate, setInitialDate] = useState(initialFormattedDate)

  const end = new Date(initialDateYear, init.getMonth() + 1, 0)
  const endDateYear = end.getFullYear()
  const endDateMonth = String(end.getMonth() + 1).padStart(2, '0')
  const endDateDay = String(end.getDate()).padStart(2, '0')
  const endFormattedDate = `${endDateYear}-${endDateMonth}-${endDateDay}`
  let [endDate, setEndDate] = useState(endFormattedDate)

  const [isLoading, setIsLoading] = useState(false)
  let [selectedDate, setSelectedDate] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  let [seriesData, setSeriesData] = useState<SeriesDataItem[]>([
    { name: 'This Year', data: [] },
    { name: 'Last Year', data: [] },
  ])

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const [isAlarmModalOpen, setIsAlarmModalOpen] = useState(false)
  const openAlarmModal = () => {
    setIsAlarmModalOpen(true)
  }

  const closeAlarmModal = () => {
    setIsAlarmModalOpen(false)
  }

  const fetchCellChartData = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = {
        isLoggedIn: true,
        companyId: companyFilter ? companyFilter.value : '',
        siteId: siteFilter,
        metersId: metersFilter,
        meterType: meterTypeFilter,
        startDate: initialDate,
        endDate: endDate,
      }
      getCellChartData({ ...params }).then((response) => {
        setTimeout(async () => {
          const data = await getCellChartData(params)
          setData(data)
          setIsLoading(false)
        }, 2000)
      })
    } catch (error) {
      console.log('Error fetching cell chart data:', error)
      setIsLoading(false)
    }
  }, [companyFilter, siteFilter, metersFilter, meterTypeFilter, initialDate, endDate])

  // Fetch company alert capability when company changes
  useEffect(() => {
    const loadCompanyAlertType = async () => {
      if (companyFilter && companyFilter.value) {
        try {
          const type = await getAlertType(Number(companyFilter.value))
          if (type === 'daily' || type === 'hourly' || type === 'both') {
            setCompanyAlertType(type)
          } else {
            setCompanyAlertType('both')
          }
        } catch (e) {
          setCompanyAlertType('both')
        }
      } else {
        setCompanyAlertType('both')
      }
    }
    loadCompanyAlertType()
  }, [companyFilter])

  // Determine effective alert type for UI (company capability and user's selection)
  const effectiveAlertType = useMemo<'daily' | 'hourly' | 'both'>(() => {
    if (!companyFilter) return 'both'
    if (companyAlertType === 'both') {
      return (settings?.alertType as 'daily' | 'hourly') || 'both'
    }
    return companyAlertType
  }, [companyFilter, companyAlertType, settings?.alertType])

  useEffect(() => {
    if (companyFilter?.label && initialDate && endDate) {
      fetchCellChartData()
    }
  }, [companyFilter, initialDate, endDate, fetchCellChartData])

  useEffect(() => {
    if (data && selectedDate && data[selectedDate]) {
      const nextThisYear = data[selectedDate]?.thisYear ?? [0]
      const nextLastYear = data[selectedDate]?.lastYear ?? [0]
      setThisYearData(nextThisYear)
      setLastYearData(nextLastYear)
      setSeriesData([
        {
          name: 'This Year',
          data: nextThisYear,
        },
        {
          name: 'Last Year',
          data: nextLastYear,
        },
      ])
    }
  }, [selectedDate, data])

  useEffect(() => {
    setMetersFilter([])
  }, [meterTypeFilter])

  const cellRender = (cellInfo: any) => {
    const date: any = cellInfo.dayNumberText
    let d = cellInfo.date
    const inputDate = new Date(d)

    const year = inputDate.getFullYear()
    const month = String(inputDate.getMonth() + 1).padStart(2, '0')
    const day = String(inputDate.getDate()).padStart(2, '0')
    const formattedDate = `${year}-${month}-${day}`
    const handleViewButtonClick = () => {
      setSelectedDate(formattedDate)
      if (companyFilter && data && data[formattedDate]) {
        setThisYearData(data[formattedDate]?.thisYear ?? [0])
        setLastYearData(data[formattedDate]?.lastYear ?? [0])
        if (thisYearData.length === 48 && lastYearData.length === 48) {
          setSeriesData([
            {
              name: 'This Year',
              data: thisYearData,
            },
            {
              name: 'Last Year',
              data: lastYearData,
            },
          ])
          openModal()
        }
      }
    }

    // replaced by per-type buttons (D/H)
    const normalizeFlag = (val: any) => val === 1 || val === '1' || val === true || val === 'true'
    const hasDaily = data && normalizeFlag(data[formattedDate]?.alarmDaily)
    const hasHourly = data && normalizeFlag(data[formattedDate]?.alarmHourly)

    const showDaily =
      effectiveAlertType === 'both' ? !!hasDaily : effectiveAlertType === 'daily' && !!hasDaily
    const showHourly =
      effectiveAlertType === 'both' ? !!hasHourly : effectiveAlertType === 'hourly' && !!hasHourly

    const statusBgColor =
      showDaily && showHourly
        ? '#f3e8ff' // light purple
        : showDaily
        ? '#FFF4CC' // light yellow
        : showHourly
        ? '#E7F1FF' // light blue
        : 'transparent'
    return (
      <div>
        {isLoading && companyFilter && !formattedDate ? (
          <div className='card-body' style={{ textAlign: 'center' }}>
            <span className='indicator-progress' style={{ display: 'block' }}>
              {intl.formatMessage({ id: 'LOADING' })}...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          </div>
        ) : companyFilter && data && data[formattedDate] ? (
          <div
            style={{
              backgroundColor: statusBgColor,
              borderRadius: '6px',
              padding: '6px',
            }}
          >
            <div
              className='d-flex align-items-center justify-content-between'
              style={{ width: '100%' }}
            >
              <div>{date}</div>
              <div className='d-flex align-items-center' style={{ gap: '6px' }}>
                {showDaily && (
                  <button
                    type='button'
                    className='btn btn-outline-warning btn-xs p-1 rounded-pill shadow-sm'
                    style={{ cursor: 'pointer', minWidth: '28px', fontWeight: 700 }}
                    title='View Daily alarm details'
                    onClick={() => {
                      setSelectedDate(formattedDate)
                      setAlarmTypeFilter('daily')
                      openAlarmModal()
                    }}
                  >
                    D
                  </button>
                )}
                {showHourly && (
                  <button
                    type='button'
                    className='btn btn-outline-primary btn-xs p-1 rounded-pill shadow-sm'
                    style={{ cursor: 'pointer', minWidth: '28px', fontWeight: 700 }}
                    title='View Half Hourly alarm details'
                    onClick={() => {
                      setSelectedDate(formattedDate)
                      setAlarmTypeFilter('hourly')
                      openAlarmModal()
                    }}
                  >
                    H
                  </button>
                )}
              </div>
            </div>
            <div className='d-none d-md-flex' style={{ justifyContent: 'space-between' }}>
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                onClick={handleViewButtonClick}
                data-bs-toggle='modal'
                data-bs-target='#kt_modal_2'
                style={{ backgroundColor: 'transparent', border: 'none' }}
              >
                <span
                  aria-hidden='true'
                  className='fa-solid fa-magnifying-glass-chart fs-4'
                  style={{ color: '#1644ce' }}
                ></span>
              </button>
              {/* Alarm action buttons are now on the top-right as D/H */}
            </div>

            <CellChart
              date={formattedDate}
              thisYearData={data[formattedDate]?.thisYear ?? [0]}
              lastYearData={data[formattedDate]?.lastYear ?? [0]}
            />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>{date}</div>
            </div>
            <div>N/A</div>
          </>
        )}
      </div>
    )
  }

  const handleDatesChange = (dates: any) => {
    const start = dates.startStr
    setIsLoading(true)
    const startDate = new Date(start)

    const startDateYear = startDate.getFullYear()
    const startDateMonth = String(startDate.getMonth() + 1).padStart(2, '0')
    const startDateDay = String(startDate.getDate()).padStart(2, '0')
    const formattedStartDate = `${startDateYear}-${startDateMonth}-${startDateDay}`

    const end = dates.endStr
    const endDate = new Date(end)

    const endDateYear = endDate.getFullYear()
    const endDateMonth = String(endDate.getMonth() + 1).padStart(2, '0')
    const endDateDay = String(endDate.getDate()).padStart(2, '0')
    const formattedEndDate = `${endDateYear}-${endDateMonth}-${endDateDay}`
    setInitialDate(formattedStartDate)
    setEndDate(formattedEndDate)
    setTimeout(() => setIsLoading(false), 300)
  }

  const calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    dayCellContent: cellRender,
    initialDate: initialDate,
    datesSet: handleDatesChange,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,dayGridWeek',
    },
  }

  return (
    <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
      <div className='col-12'>
        <FilterFormCalendar
          companyFilter={companyFilter}
          setCompanyFilter={setCompanyFilter}
          siteFilter={siteFilter}
          setSiteFilter={setSiteFilter}
          metersFilter={metersFilter}
          setMetersFilter={setMetersFilter}
          meterTypeFilter={meterTypeFilter}
          setMeterTypeFilter={setMeterTypeFilter}
          selectedMeters={selectedMeters}
          setSelectedMeters={setSelectedMeters}
        />
      </div>
      <div className='card'>
        {isLoading && (
          <div
            className='position-absolute w-100 h-100 d-flex align-items-center justify-content-center'
            style={{ backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 2 }}
          >
            <span className='spinner-border text-primary'></span>
          </div>
        )}
        <div className='px-5 pt-5'>
          <div className='d-flex align-items-center flex-wrap' style={{ gap: '20px' }}>
            {effectiveAlertType !== 'hourly' && (
              <div className='d-flex align-items-center'>
                <div
                  className='me-2'
                  style={{
                    position: 'relative',
                    width: '24px',
                    height: '20px',
                    borderRadius: '6px',
                    backgroundColor: '#FFF4CC',
                    border: '1px solid #FFC107',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      fontSize: '9px',
                      fontWeight: 700,
                      lineHeight: 1,
                      padding: '1px 4px',
                      borderRadius: '999px',
                      border: '1px solid #B58100',
                      color: '#B58100',
                      backgroundColor: '#fff',
                    }}
                  >
                    D
                  </span>
                </div>
                <span className='text-gray-600'>Daily Alert</span>
              </div>
            )}
            {effectiveAlertType !== 'daily' && (
              <div className='d-flex align-items-center'>
                <div
                  className='me-2'
                  style={{
                    position: 'relative',
                    width: '24px',
                    height: '20px',
                    borderRadius: '6px',
                    backgroundColor: '#E7F1FF',
                    border: '1px solid #0d6efd',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      fontSize: '9px',
                      fontWeight: 700,
                      lineHeight: 1,
                      padding: '1px 4px',
                      borderRadius: '999px',
                      border: '1px solid #0b5ed7',
                      color: '#0b5ed7',
                      backgroundColor: '#fff',
                    }}
                  >
                    H
                  </span>
                </div>
                <span className='text-gray-600'>Half Hourly Alert</span>
              </div>
            )}
            {effectiveAlertType === 'both' && (
              <div className='d-flex align-items-center'>
                <div
                  className='me-2'
                  style={{
                    position: 'relative',
                    width: '30px',
                    height: '20px',
                    borderRadius: '6px',
                    backgroundColor: '#f3e8ff',
                    border: '1px solid #6f42c1',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      fontSize: '9px',
                      fontWeight: 700,
                      lineHeight: 1,
                      padding: '1px 4px',
                      borderRadius: '999px',
                      border: '1px solid #6f42c1',
                      color: '#6f42c1',
                      backgroundColor: '#fff',
                    }}
                  >
                    D H
                  </span>
                </div>
                <span className='text-gray-600'>Both</span>
              </div>
            )}
            <div className='d-flex align-items-center'>
              <div className='text-gray-600' style={{ fontSize: '12px' }}>
                <span className='fa-solid fa-circle-info text-gray-500'></span>{' '}
                {effectiveAlertType === 'daily'
                  ? 'Click D button in any day cell to view daily alerts'
                  : effectiveAlertType === 'hourly'
                  ? 'Click H button in any day cell to view half hourly alerts'
                  : 'Click D or H button in any day cell to view alert details'}
              </div>
            </div>
          </div>
        </div>
        <div className='calendar-container' style={{ marginTop: '10px' }}>
          <FullCalendar {...calendarOptions} />
        </div>
      </div>

      <ChartViewModalForCalendar
        isOpen={isModalOpen}
        date={selectedDate}
        data={seriesData}
        onClose={closeModal}
      />

      <CalendarAlarmViewModal
        isOpen={isAlarmModalOpen}
        onClose={closeAlarmModal}
        date={selectedDate}
        company={companyFilter}
        companySites={siteFilter}
        meters={metersFilter}
        alarmType={alarmTypeFilter}
      />
    </div>
  )
}

export default CalendarViewPage
